import { Injectable, signal } from '@angular/core';
import { DraftState } from './agent-builder.interface';
import { ConversationTurn } from './feedback.service';

export type SaveIndicator = 'idle' | 'saving' | 'saved' | 'error';

/** Readiness-coach checklist (mockup v3): the topbar pill renders it and
 *  each unmet item jumps to the section that fixes it. */
export interface ReadinessItem {
  label: string;
  ok: boolean;
  section: string | null;
}

export interface Readiness {
  items: ReadinessItem[];
  score: number;
  total: number;
}

export interface BuilderContext {
  name: string;
  /** Draft id — rides along as agent_id context on feedback submissions. */
  draftId?: string;
  /** Profile accent — tints the breadcrumb's initial-letter avatar. */
  color?: string;
  state: DraftState;
  saveState: SaveIndicator;
  canSubmit: boolean;
  submitLabel: string;
  /** Opens the pre-flight submit modal inside the builder. */
  onSubmit: () => void;
  /** Withdraw a pending submission (only meaningful in pending_approval). */
  onWithdraw?: () => void;
  /** Readiness coach — rendered as a pill next to the submit action. */
  readiness?: Readiness;
  /** Jump to a builder section (readiness checklist rows). */
  onJumpSection?: (section: string) => void;
  /** Snapshot of the co-builder transcript for the Send Feedback dialog —
   *  called when the dialog OPENS (not reactively), so the previewed
   *  conversation is stable while the form is up. Returns [] when there's
   *  nothing worth attaching (e.g. only the canned greeting). */
  feedbackConversation?: () => ConversationTurn[];
}

/**
 * Lets the builder page project its breadcrumb + submit action into the
 * shell topbar (the redesign keeps a single topbar instead of a second
 * in-page header). The builder sets this on init/update and clears it on
 * destroy; the shell only reads.
 */
@Injectable({ providedIn: 'root' })
export class ShellContextService {
  readonly builder = signal<BuilderContext | null>(null);

  setBuilder(ctx: BuilderContext): void {
    this.builder.set(ctx);
  }

  clearBuilder(): void {
    this.builder.set(null);
  }
}
